// Règles d'affichage du questionnaire ECF (spec 010) : lecture seule (E11), fichiers (E9, E10),
// statut affiché (data-model §3), complétude et pièces attendues (012 data-model §1) ;
// questions conditionnelles et dates (spec 011, data-model §2).
import {
  EcfAnswer, EcfDefinition, EcfExpectedFile, EcfManager, EcfPoint, EcfQuestion,
  EcfQuestionnaireDetail, EcfQuestionnaireSummary,
} from '@shared/models/ecf-questionnaire/ecf-questionnaire.model';

/** 10 × 1024 × 1024 octets, même valeur dans le front, le relais et Décisiel (E10). */
export const MAX_FILE_SIZE = 10_485_760;
export const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.doc', '.docx', '.xls', '.xlsx', '.csv'];

type Answers = Record<string, EcfAnswer>;

/** Réponses, brouillon, dépôt, retrait, envoi : Décisiel le permet et pas en Connexion externe (E11). */
export function canEdit(q: Pick<EcfQuestionnaireDetail, 'CanEdit'> | null | undefined, isExternal: boolean): boolean {
  return !!q && q.CanEdit && !isExternal;
}

/** Dépôt tardif : Décisiel le permet et pas en Connexion externe (E11). */
export function canUploadLate(q: Pick<EcfQuestionnaireDetail, 'CanUploadLate'> | null | undefined, isExternal: boolean): boolean {
  return !!q && q.CanUploadLate && !isExternal;
}

export function isFileAccepted(name: string, size: number): boolean {
  const dot = name.lastIndexOf('.');
  const extension = dot < 0 ? '' : name.substring(dot).toLowerCase();
  return ALLOWED_EXTENSIONS.includes(extension) && size <= MAX_FILE_SIZE;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024)
    return `${bytes} o`;
  if (bytes < 1024 * 1024)
    return `${Math.round(bytes / 1024).toLocaleString('fr-FR')} Ko`;
  return `${(bytes / (1024 * 1024)).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
}

/** Gestionnaire prévenu (FR-011) : texte sans nom si Manager est null ou sans nom (garde-fou du 13/10). */
export function managerNotice(manager: EcfManager | null | undefined): string {
  const name = manager?.Name?.trim();
  return name ? `Votre gestionnaire ${name} a été prévenu.` : 'Votre gestionnaire a été prévenu.';
}

/** Ligne de contact : null (masquée) si Manager est null ou sans e-mail ; nom null si vide. */
export function managerContact(manager: EcfManager | null | undefined): { name: string | null; email: string } | null {
  const email = manager?.Email?.trim();
  if (!email)
    return null;
  return { name: manager.Name?.trim() || null, email };
}

export function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' }) : '';
}

export function displayStatus(q: EcfQuestionnaireSummary): { label: string; action: 'Continuer' | 'Consulter' } {
  if (q.Status === 'Sent')
    return q.HasDraft
      ? { label: `Brouillon · ${q.CompletedPoints}/${q.TotalPoints}`, action: 'Continuer' }
      : { label: 'À compléter', action: 'Continuer' };

  let label = `Envoyé le ${formatDate(q.SubmittedDate)}`;
  if (q.Status === 'Received' && q.MissingFilesCount > 0)
    label += q.MissingFilesCount === 1 ? ' · 1 pièce manquante' : ` · ${q.MissingFilesCount} pièces manquantes`;
  return { label, action: 'Consulter' };
}

export function isDetailShown(question: EcfQuestion, answer: EcfAnswer | undefined): boolean {
  return !!question.detail && answer?.Value === question.detail.when;
}

export function isAnswered(question: EcfQuestion, answer: EcfAnswer | undefined): boolean {
  if (!answer?.Value?.trim())
    return false;
  return !(isDetailShown(question, answer) && question.detail.required && !answer.Detail?.trim());
}

export function unansweredCodes(definition: EcfDefinition, answers: Answers): string[] {
  const questions = definition.points.flatMap(p => p.questions);
  const hidden = hiddenCodes(questions, answers);
  return questions
    .filter(q => !hidden.has(q.code) && !isAnswered(q, answers[q.code]))
    .map(q => q.code);
}

export function isPointComplete(point: EcfPoint, answers: Answers): boolean {
  return shownQuestions(point, answers).every(q => isAnswered(q, answers[q.code]));
}

export function completedPoints(definition: EcfDefinition, answers: Answers): number {
  return definition.points.filter(p => isPointComplete(p, answers)).length;
}

export function shownExpectedFiles(point: EcfPoint, answers: Answers): EcfExpectedFile[] {
  return point.expectedFiles.filter(f =>
    !f.when || Object.entries(f.when).every(([code, value]) => answers[code]?.Value === value));
}

/**
 * Questions masquées, comme Décisiel 017 (research D1) : dans l'ordre, une question est masquée si une condition
 * de son when vise une question déjà masquée ou une autre réponse (égalité exacte).
 */
export function hiddenCodes(questions: EcfQuestion[], answers: Answers): Set<string> {
  const hidden = new Set<string>();
  for (const q of questions) {
    const met = Object.entries(q.when ?? {}).every(([code, value]) => !hidden.has(code) && answers[code]?.Value === value);
    if (!met)
      hidden.add(q.code);
  }
  return hidden;
}

export function shownQuestions(point: EcfPoint, answers: Answers): EcfQuestion[] {
  const hidden = hiddenCodes(point.questions, answers);
  return point.questions.filter(q => !hidden.has(q.code));
}

/** Réponses sans celles des questions masquées : ni gardées ni envoyées (FR-004, FR-005). */
export function withoutHiddenAnswers(definition: EcfDefinition | null | undefined, answers: Answers): Answers {
  const hidden = hiddenCodes(definition?.points?.flatMap(p => p.questions) ?? [], answers);
  return Object.fromEntries(Object.entries(answers).filter(([code]) => !hidden.has(code)));
}

export function isDateQuestion(question: EcfQuestion): boolean {
  return question.type === 'Text' && question.format === 'date';
}

/** jj/mm/aaaa ou j/m/aaaa → date locale ; null si illisible ou impossible (31/02). */
export function parseDateText(text: string | null | undefined): Date | null {
  const match = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/.exec(text ?? '');
  if (!match)
    return null;
  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

export function formatDateText(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/** Points désignés pour le client : « numéro · titre » de la définition, sans doublon (architecture §5.3). */
export function pointLabels(codes: string[], definition?: EcfDefinition | null): string[] {
  const pointCodes = [...new Set(codes.map(c => c.split('.')[0]))];
  return pointCodes.map(code => {
    const point = definition?.points?.find(p => p.code === code);
    return point ? `${code} · ${point.title}` : code;
  });
}

/**
 * Points où le dépôt est proposé : chaque point tant que le questionnaire est modifiable ; après l'envoi,
 * seulement les points d'une pièce manquante, selon MissingFiles de Décisiel (FR-007, FR-013, E11).
 */
export function uploadPointCodes(
  q: Pick<EcfQuestionnaireDetail, 'Definition' | 'CanEdit' | 'CanUploadLate' | 'MissingFiles'> | null | undefined,
  isExternal: boolean): string[] {
  if (canEdit(q, isExternal))
    return q.Definition?.points?.map(p => p.code) ?? [];
  if (canUploadLate(q, isExternal))
    return [...new Set((q.MissingFiles ?? []).map(m => m.Point))];
  return [];
}
